import jsPDF from "jspdf";
import emailjs from "@emailjs/browser";
import logo from "./LogoG.jpg";

// Helper function to compress images
const compressImage = (base64String, maxWidth = 800, quality = 0.7) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Calculate new dimensions maintaining aspect ratio
      let { width, height } = img;
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
      resolve(compressedBase64);
    };
    img.src = base64String;
  });
};

const formatDateToYYYYMMDD = (date) => {
  const yyyy = date.getFullYear();
  let mm = date.getMonth() + 1; // Enero es 0!
  let dd = date.getDate();

  // Añadir ceros adelante si el mes o día son menores a 10
  if (mm < 10) mm = "0" + mm;
  if (dd < 10) dd = "0" + dd;

  return `${yyyy}-${mm}-${dd}`;
};

const generatePDFCC = async (cardInfo, amount, invoiceNumber) => {
  // Compress images first to reduce PDF size
  let compressedIdPhoto = null;
  let compressedSignature = null;
  
  if (cardInfo.idPhoto) {
    try {
      compressedIdPhoto = await compressImage(cardInfo.idPhoto, 400, 0.6);
    } catch (error) {
      console.error("Error compressing ID photo:", error);
      compressedIdPhoto = cardInfo.idPhoto; // Use original if compression fails
    }
  }
  
  if (cardInfo.signature) {

      compressedSignature = cardInfo.signature; // Use original if compression fails

  }

  const doc = new jsPDF();
  const selectedCardTypes = Object.keys(cardInfo.cardType)
    .filter((cardType) => cardInfo.cardType[cardType])
    .join(", ");

  const title = "CREDIT/DEBIT CARD DETAILS";
  const titleWidth =
    (doc.getStringUnitWidth(title) * doc.internal.getFontSize()) /
    doc.internal.scaleFactor;
  const pageWidth2 = doc.internal.pageSize.getWidth();
  const titleX = (pageWidth2 - titleWidth) / 2;
  doc.text(title, titleX, 10);

  doc.setFontSize(12);

  const normalizeAmount = (value) => {
    const clean = String(value ?? "")
      .replace(/[$,\s]/g, "")     // quita $, comas y espacios
      .replace(/[^\d.-]/g, "");   // deja solo números, punto y signo

    const num = Number(clean || 0);

    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatAmount = normalizeAmount(amount);

  const authorizationText = `I authorize GLOBAL TRANSIT SOLUTIONS LLC to charge my credit card in the amount of: $${formatAmount} `;
  const maxLineWidth = pageWidth2 - 20; // considering margins of 10 units on each side

  const wrappedText = doc.splitTextToSize(authorizationText, maxLineWidth);

  const currentDate = formatDateToYYYYMMDD(new Date());

  const data = [
    { label: "Rep. Name", value: cardInfo.agentName },
    { label: "Invoice Number", value: invoiceNumber },
    { label: "Date", value: currentDate },
    { label: "Card Type", value: selectedCardTypes },
    { label: "Card Holder's Name", value: cardInfo.cardName },
    { label: "Card Number", value: cardInfo.cardNumber },
    { label: "Expiry Date", value: cardInfo.expiryDate },
    { label: "CVV", value: cardInfo.cvv },
    { label: "Billing Address", value: cardInfo.billingAddress },
    { label: "City", value: cardInfo.city },
    { label: "State", value: cardInfo.state },
    { label: "Zip Code", value: cardInfo.zipCode },
    { label: "Phone", value: cardInfo.phone },
    { label: "Authorize", value: wrappedText, multiline: true },
    { label: "Printed Name", value: cardInfo.printedName },
  ];
  doc.setFontSize(12);
  const logoHeight = 25; 
  let yPos = logoHeight;
  const columnSpacing = 60;

  const rowHeight = 10;

  const tableWidth = doc.internal.pageSize.getWidth() - 20;
  data.forEach((item) => {
    if (item.multiline) {
      // Handle multiline text (like Authorization)
      const lines = doc.splitTextToSize(item.value, tableWidth - columnSpacing - 10);
      const currentRowHeight = lines.length * rowHeight;
      doc.rect(10, yPos, tableWidth, currentRowHeight);
      doc.rect(10, yPos, columnSpacing, currentRowHeight);
      doc.text(item.label, 15, yPos + 7);
      doc.text(lines, 75, yPos + 7);
      yPos += currentRowHeight;
    } else {
      const currentRowHeight = rowHeight;
      doc.rect(10, yPos, tableWidth, currentRowHeight);
      doc.rect(10, yPos, columnSpacing, currentRowHeight);
      doc.text(item.label, 15, yPos + 7);
      doc.text(item.value ? item.value.toString() : "", 75, yPos + 7);
      yPos += currentRowHeight;
    }
  });

  // Agregar logo
  const pageWidth = doc.internal.pageSize.getWidth();
  const logoSize = 30; // Square dimensions for the logo
  doc.addImage(logo, "JPEG", pageWidth - 40, 0, logoSize, logoSize);

  // Add ID Photo section
  if (compressedIdPhoto) {
    try {
      const idPhotoRowHeight = 60;
      doc.rect(10, yPos, tableWidth, idPhotoRowHeight);
      doc.rect(10, yPos, columnSpacing, idPhotoRowHeight);
      doc.text("ID Photo", 15, yPos + 7);
      
      let idPhotoData = compressedIdPhoto;
      if (idPhotoData.includes('data:image/')) {
        // Extract the base64 data and format
        const format = idPhotoData.match(/data:image\/(\w+);base64,/);
        if (format) {
          idPhotoData = idPhotoData.replace(`data:image/${format[1]};base64,`, '');
          
          const idPhotoHeight = 50;
          const idPhotoWidth = 80;
          const idPhotoX = 75;
          const idPhotoY = yPos + 5;
          
          doc.addImage(
            idPhotoData,
            format[1].toUpperCase(),
            idPhotoX,
            idPhotoY,
            idPhotoWidth,
            idPhotoHeight,
            undefined,
            'FAST'
          );
        }
      }
      
      yPos += idPhotoRowHeight;
    } catch (error) {
      console.error("Error adding ID photo:", error);
    }
  }

  // Add signature section
  const signatureRowHeight = 40;
  if (compressedSignature) {
    try {
      doc.rect(10, yPos, tableWidth, signatureRowHeight);
      doc.rect(10, yPos, columnSpacing, signatureRowHeight);
      const signatureHeight = 30;
      const signatureY = yPos + 5;
      doc.text("Signature", 15, yPos + 7);
      
      let signatureData = compressedSignature;
      if (signatureData.includes('data:image/jpeg;base64,')) {
        signatureData = signatureData.replace('data:image/jpeg;base64,', '');
      }
      
      const signatureWidth = 100;
      const signatureX = (pageWidth2 - signatureWidth) / 2;
      
      doc.addImage(
        signatureData,
        'JPEG',
        signatureX,
        signatureY,
        signatureWidth,
        signatureHeight,
        undefined,
        'FAST'
      );
      
      yPos += signatureRowHeight;
    } catch (error) {
      console.error("Error adding signature:", error);
    }
  }

  // Keep this section at the bottom after signature
  yPos += 15;

  const authorizationTextBottom =
    "I authorize GLOBAL TRANSIT SOLUTIONS LLC to charge my credit card for the amount listed above";

  const bottomMaxWidth = pageWidth2 - 20;
  const bottomLines = doc.splitTextToSize(authorizationTextBottom, bottomMaxWidth);

  const lineHeight = 6;
  const bottomTextHeight = bottomLines.length * lineHeight;
  const pageHeight = doc.internal.pageSize.getHeight();

  if (yPos + bottomTextHeight > pageHeight - 10) {
    doc.addPage();
    yPos = 20;
  }

  doc.text(bottomLines, 10, yPos);

  // Convert PDF to base64 string
  const pdfBase64 = doc.output('datauristring');

  try {
    // Send single email with complete PDF
    const emailData = {
      to_email: "admin@globaltransitsol.com",
      from_name: cardInfo.cardName || "Credit Card Form",
      invoice_number: invoiceNumber,
      amount: amount,
      content: pdfBase64,
      message: `Credit card form submission for Invoice #${invoiceNumber}`,
    };

    await emailjs.send(
      "service_f9cgj1h",
      "template_3xty2oh",
      emailData,
      "AayvUU2KmVcoVafZF"
    );
    
    return true;
  } catch (error) {
    console.error("Error sending email:", error);
    throw error;
  }
};

// Helper function to compress images

// Helper function to split large base64 strings - REMOVED (no longer needed)

export default generatePDFCC;
